import { createContext, useCallback, useContext, useMemo } from "react";
import type {
  ConversationStatus,
  ConversationID,
  Conversation,
} from "@sdr/schemas/conversation";
import type { Message, NewUserMessage } from "@sdr/schemas/message";
import type { PublicAssistant } from "@sdr/schemas/assistants";

import { useLocalStorage } from "../hooks/use-local-storage";
import { trpc } from "../trpc";
import { ErrorBoundary } from "../error-boundary";

export type UseChatOptions = {
  api?: string;
  preview?: boolean;
  onError?: (error: Error) => void;
};

export type UseChatHelpers = {
  input: string;
  messages: Message[];
  error?: unknown;
  isLoading: boolean;
  status: ConversationStatus;
  append: (content: string, value?: any) => Promise<void>;
  reset: () => void;
  assistant: PublicAssistant;
  setInput: (content: string) => void;
};

export type UseChatState = {
  id?: ConversationID;
  messages: Message[];
  status: ConversationStatus;
  isLoading: boolean;
  error?: unknown;
  input: string;
};

const initialState = (assistant: PublicAssistant): UseChatState => ({
  id: undefined,
  messages: [
    {
      id: 'msg_welcome',
      role: 'assistant',
      content: assistant.welcome || 'Olá! Como posso te ajudar hoje?',
      toolCalls: [],
      toolCallId: null,
      conversationId: undefined!,
      createdAt: new Date(),
      updatedAt: new Date(),
      metadata: {
        welcome: true,
      },
    },
  ],
  status: "active",
  isLoading: false,
  error: undefined,
  input: "",
})

type Action<P = void> = (payload: P) => (state: UseChatState) => UseChatState;

const startConversation: Action<Conversation> = (conversation) => (state) => ({
  ...state,
  id: conversation.id,
});

const sendMessage: Action<NewUserMessage> = (message) => (state) => ({
  ...state,
  input: "",
  isLoading: true,
  messages: [message as Message, ...state.messages],
});

const receiveReply: Action<Message> = (message) => (state) => ({
  ...state,
  id: message.conversationId,
  isLoading: false,
  messages: [message, ...state.messages],
});

const resetConversation: Action<PublicAssistant> = (assistant: PublicAssistant) => () => initialState(assistant);

const handleError = (error: unknown) => (state: UseChatState) => ({
  ...state,
  error,
  isLoading: false,
});

export const ChatContext = createContext<UseChatHelpers>(undefined!);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error("useChat must be used inside an ChatProvider");
  return context;
};

export function ChatProvider({
  children,
  assistant,
}: {
  children: React.ReactNode;
  assistant: PublicAssistant;
}) {
  const key = `chat_${assistant.id}`;

  const [state, setState] = useLocalStorage(key, initialState(assistant));

  const chat = trpc.chat.ask.useMutation({
    onError: (e) => {
      setState(handleError(e));
    },
  });

  const start = trpc.chat.start.useMutation({
    onError: (e) => {
      setState(handleError(e));
    },
  });

  const append = useCallback(
    async (content: string, value?: any): Promise<void> => {
      const message: NewUserMessage = {
        role: "user",
        content,
        metadata: { value },
      };
      setState(sendMessage(message));

      let id = state.id;
      if (!id) {
        const conversation = await start.mutateAsync({
          assistantId: assistant.id,
        });
        setState(startConversation(conversation));
        id = conversation.id;
      }
      const response = await chat.mutateAsync({ id, message });
      setState(receiveReply(response!));
    },
    [assistant, state.id],
  );

  const reset = useCallback(() => {
    setState(resetConversation(assistant));
  }, [assistant]);

  const setInput = useCallback(
    (content: string) => {
      setState((prev) => ({ ...prev, input: content || "" }));
    },
    [state.input],
  );

  const helpers = useMemo(
    () => ({
      ...state,
      isLoading: start.isLoading || chat.isLoading,
      append,
      reset,
      assistant,
      setInput,
    }),
    [
      state,
      append,
      reset,
      setInput,
      chat.isLoading,
      start.isLoading,
      assistant,
    ],
  );

  return (
    <ChatContext.Provider value={helpers}>
      <ErrorBoundary onReset={reset}>
        {children}
      </ErrorBoundary>
    </ChatContext.Provider>
  );
}
